import { uniq } from 'es-toolkit';

import {
  ProductionArtifactsContext,
  ProductionOutputParameters,
  ProductionOutputType,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { hasTrePreservePlateTypePermission } from '@paragon/login-tools';
import { getPlateManufacturer } from '@paragon/material-lists';
import { enqueueError, enqueueWarning } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { getProductionComponentInstancesByBundleGuid } from '../selectors/getProductionComponentInstancesByBundleGuid';
import { getLoadedTrussesByAnalysisSetGuid } from '../selectors/trusses';
import { AsyncThunkActionVoid } from './thunkActions';

export function downloadProductionArtifacts(
  bundleGuids: readonly string[],
  productionOutputTypes: readonly ProductionOutputType[],
  productionArtifactsContext: ProductionArtifactsContext,
  productionOutputParameters: ProductionOutputParameters,
): AsyncThunkActionVoid {
  return async function downloadProductionArtifactsThunkAction(dispatch, getState) {
    const spinnerId = dispatch(addSpinnerMessage('Downloading production output ...'));

    if (productionOutputTypes.includes('TreFiles') && !hasTrePreservePlateTypePermission(getState())) {
      const productionComponentInstancesByBundleGuid = getProductionComponentInstancesByBundleGuid(getState());
      const analysisSetGuids = uniq(
        bundleGuids
          .flatMap((bundleGuid) => productionComponentInstancesByBundleGuid[bundleGuid])
          .map((instance) => instance.analysisSetGuid),
      );
      const loadedTrussesByAnalysisSetGuid = getLoadedTrussesByAnalysisSetGuid(getState());
      if (
        analysisSetGuids.some((analysisSetGuid) => {
          const loadedTruss = loadedTrussesByAnalysisSetGuid[analysisSetGuid];
          if (!loadedTruss) return false; // Do not show warning if truss is not loaded
          return loadedTruss.platePairs.some((platePair) => {
            const plateManufacturer = getPlateManufacturer(platePair.plateType);
            return plateManufacturer !== 'Any' && plateManufacturer !== 'MiTek';
          });
        })
      ) {
        dispatch(enqueueWarning('Plates are being converted to MiTek plates in TRE file(s)'));
      }
    }

    try {
      const file = await designService.bundles.downloadProductionArtifacts(
        bundleGuids,
        productionOutputTypes,
        productionArtifactsContext,
        productionOutputParameters,
      );
      file.download();
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error downloading'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function downloadBillOfLading(
  truckloadGuid: string,
  salesperson: string | null,
  soldToAddress: string | null,
  shipToAddress: string | null,
): AsyncThunkActionVoid {
  return async function downloadBillOfLadingThunkAction(dispatch) {
    const spinnerId = dispatch(addSpinnerMessage('Downloading bill of lading ...'));

    try {
      const file = await designService.truckloads.downloadBillOfLading(
        truckloadGuid,
        salesperson,
        soldToAddress,
        shipToAddress,
      );
      file.download();
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error downloading bill of lading'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}
